import { apiClient } from './api-client';
import {
  Expense,
  ExpenseCategory,
  RecurringExpenseRule,
  ExpenseReminder,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  CreateRecurringExpenseRequest,
  UpdateRecurringExpenseRequest,
  CreateExpenseCategoryRequest,
  ExpenseListRequest,
  ExpenseReportRequest,
  ExpenseListResponse,
  RecurringExpenseListResponse,
  ExpenseCategoriesResponse,
  ExpenseReportResponse,
  ExpenseReminderNotification,
  ExpenseNotification,
  ExpenseNotificationsResponse,
  ExpenseNotificationPreferences,
  ExpenseSummary,
  APIResponse
} from '../types';
import { ExpenseErrorHandler, ExpenseError } from './expense-error-handling';

// Performance optimization: Request caching
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Helper functions for caching
const getCacheKey = (url: string, params?: any) => {
  return `${url}${params ? `?${JSON.stringify(params)}` : ''}`;
};

const getCachedResponse = (key: string) => {
  const cached = requestCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  if (cached) {
    requestCache.delete(key);
  }
  return null;
};

const setCachedResponse = (key: string, data: any) => {
  // Limit cache size to prevent memory leaks
  if (requestCache.size >= 100) {
    const oldestKey = requestCache.keys().next().value;
    if (oldestKey) {
      requestCache.delete(oldestKey);
    }
  }
  requestCache.set(key, { data, timestamp: Date.now() });
};

export class ExpensesService {
  // ===== EXPENSE CRUD OPERATIONS =====

  // Get all expenses for the current user with filtering and caching
  static async getExpenses(filters?: ExpenseListRequest): Promise<ExpenseListResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.listingId) params.append('listingId', filters.listingId);
      if (filters?.listingIds?.length) {
        filters.listingIds.forEach(id => params.append('listingIds', id));
      }
      if (filters?.categoryId) params.append('categoryId', filters.categoryId);
      if (filters?.categoryIds?.length) {
        filters.categoryIds.forEach(id => params.append('categoryIds', id));
      }
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.recurrenceType) params.append('recurrenceType', filters.recurrenceType);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.minAmount !== undefined) params.append('minAmount', filters.minAmount.toString());
      if (filters?.maxAmount !== undefined) params.append('maxAmount', filters.maxAmount.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());
      if (filters?.lastEvaluatedKey) params.append('lastEvaluatedKey', filters.lastEvaluatedKey);

      const queryString = params.toString();
      const url = queryString ? `/expenses?${queryString}` : '/expenses';
      
      // Only cache GET requests without pagination for performance
      const shouldCache = !filters?.offset && !filters?.lastEvaluatedKey && !filters?.search;
      
      if (shouldCache) {
        const cacheKey = getCacheKey(url);
        const cached = getCachedResponse(cacheKey);
        
        if (cached) {
          return cached;
        }
      }
      
      const response = await apiClient.get<ExpenseListResponse>(url);
      
      if (shouldCache) {
        const cacheKey = getCacheKey(url);
        setCachedResponse(cacheKey, response.data);
      }
      
      return response.data;
    } catch (error) {
      const expenseError = ExpenseErrorHandler.parseApiError(error);
      ExpenseErrorHandler.logError(expenseError, { 
        operation: 'getExpenses', 
        filters 
      });
      throw error;
    }
  }

  // Search expenses by description or other text fields
  static async searchExpenses(query: string, options: { limit?: number } = {}): Promise<ExpenseListResponse> {
    const params = new URLSearchParams();
    params.append('search', query);
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get<ExpenseListResponse>(`/expenses/search?${params.toString()}`);
    return response.data;
  }

  // Get a specific expense by ID
  static async getExpense(expenseId: string): Promise<Expense> {
    const response = await apiClient.get<Expense>(`/expenses/${expenseId}`);
    return response.data;
  }

  // Create a new expense
  static async createExpense(expenseData: CreateExpenseRequest): Promise<Expense> {
    try {
      // Client-side validation before sending to server
      const validation = this.validateExpense(expenseData);
      if (!validation.isValid) {
        const errorMessage = validation.errors.join(', ');
        throw new Error(`Validation failed: ${errorMessage}`);
      }

      const response = await apiClient.post<Expense>('/expenses', expenseData);
      
      // Invalidate relevant cache entries
      this.clearCacheForPattern('/expenses');
      
      return response.data;
    } catch (error) {
      const expenseError = ExpenseErrorHandler.parseApiError(error);
      ExpenseErrorHandler.logError(expenseError, { 
        operation: 'createExpense', 
        expenseData: { 
          ...expenseData, 
          description: expenseData.description?.substring(0, 50) // Truncate for logging
        }
      });
      throw error;
    }
  }

  // Update an existing expense
  static async updateExpense(expenseId: string, expenseData: UpdateExpenseRequest): Promise<Expense> {
    const response = await apiClient.put<Expense>(`/expenses/${expenseId}`, expenseData);
    
    // Invalidate relevant cache entries
    this.clearCacheForPattern('/expenses');
    
    return response.data;
  }

  // Delete an expense
  static async deleteExpense(expenseId: string): Promise<void> {
    await apiClient.delete(`/expenses/${expenseId}`);
    
    // Invalidate relevant cache entries
    this.clearCacheForPattern('/expenses');
  }

  // ===== EXPENSE CATEGORIES =====

  // Get all expense categories (predefined and custom) with caching
  static async getExpenseCategories(): Promise<ExpenseCategoriesResponse> {
    const cacheKey = getCacheKey('/expenses/categories');
    const cached = getCachedResponse(cacheKey);
    
    if (cached) {
      return cached;
    }
    
    const response = await apiClient.get<ExpenseCategoriesResponse>('/expenses/categories');
    setCachedResponse(cacheKey, response.data);
    return response.data;
  }

  // Create a custom expense category
  static async createExpenseCategory(categoryData: CreateExpenseCategoryRequest): Promise<ExpenseCategory> {
    const response = await apiClient.post<{ message: string; category: ExpenseCategory }>('/expenses/categories', categoryData);
    return response.data.category;
  }

  // Update a custom expense category
  static async updateExpenseCategory(categoryId: string, categoryData: Partial<CreateExpenseCategoryRequest>): Promise<ExpenseCategory> {
    const response = await apiClient.put<ExpenseCategory>(`/expenses/categories/${categoryId}`, categoryData);
    return response.data;
  }

  // Delete a custom expense category
  static async deleteExpenseCategory(categoryId: string): Promise<void> {
    await apiClient.delete(`/expenses/categories/${categoryId}`);
  }

  // ===== RECURRING EXPENSES =====

  // Get all recurring expense rules for the current user
  static async getRecurringExpenses(): Promise<RecurringExpenseListResponse> {
    const response = await apiClient.get<RecurringExpenseListResponse>('/expenses/recurring');
    return response.data;
  }

  // Get a specific recurring expense rule by ID
  static async getRecurringExpense(recurringExpenseId: string): Promise<RecurringExpenseRule> {
    const response = await apiClient.get<RecurringExpenseRule>(`/expenses/recurring/${recurringExpenseId}`);
    return response.data;
  }

  // Create a new recurring expense rule
  static async createRecurringExpense(recurringExpenseData: CreateRecurringExpenseRequest): Promise<RecurringExpenseRule> {
    const response = await apiClient.post<RecurringExpenseRule>('/expenses/recurring', recurringExpenseData);
    return response.data;
  }

  // Update an existing recurring expense rule
  static async updateRecurringExpense(recurringExpenseId: string, recurringExpenseData: UpdateRecurringExpenseRequest): Promise<RecurringExpenseRule> {
    const response = await apiClient.put<RecurringExpenseRule>(`/expenses/recurring/${recurringExpenseId}`, recurringExpenseData);
    return response.data;
  }

  // Delete (deactivate) a recurring expense rule
  static async deleteRecurringExpense(recurringExpenseId: string): Promise<void> {
    await apiClient.delete(`/expenses/recurring/${recurringExpenseId}`);
  }

  // ===== EXPENSE REPORTS AND ANALYTICS =====

  // Generate expense reports with filtering and grouping
  static async getExpenseReports(reportRequest: ExpenseReportRequest): Promise<ExpenseReportResponse> {
    const response = await apiClient.post<ExpenseReportResponse>('/expenses/reports', reportRequest);
    return response.data;
  }

  // Export expense data in specified format
  static async exportExpenseData(reportRequest: ExpenseReportRequest): Promise<Blob> {
    // Note: This method would need to be implemented with proper blob handling
    // For now, we'll throw an error indicating it needs implementation
    throw new Error('Export functionality needs to be implemented with proper blob handling');
  }

  // Get expense summary for dashboard
  static async getExpenseSummary(listingIds?: string[]): Promise<ExpenseSummary> {
    const params = new URLSearchParams();
    if (listingIds && listingIds.length > 0) {
      listingIds.forEach(id => params.append('listingIds', id));
    }

    const queryString = params.toString();
    const url = queryString ? `/expenses/summary?${queryString}` : '/expenses/summary';
    
    const response = await apiClient.get<ExpenseSummary>(url);
    return response.data;
  }

  // ===== EXPENSE REMINDERS =====

  // Get pending expense reminders
  static async getExpenseReminders(): Promise<ExpenseReminderNotification[]> {
    const response = await apiClient.get<ExpenseReminderNotification[]>('/expenses/reminders');
    return response.data;
  }

  // Complete an expense reminder by creating the actual expense
  static async completeExpenseReminder(reminderId: string, expenseData: CreateExpenseRequest): Promise<Expense> {
    const response = await apiClient.post<Expense>(`/expenses/reminders/${reminderId}/complete`, expenseData);
    return response.data;
  }

  // Dismiss an expense reminder
  static async dismissExpenseReminder(reminderId: string): Promise<void> {
    await apiClient.post(`/expenses/reminders/${reminderId}/dismiss`);
  }

  // ===== EXPENSE NOTIFICATIONS =====

  // Get expense notifications for the current user
  static async getExpenseNotifications(options?: {
    limit?: number;
    page?: number;
    type?: string;
    status?: string;
    priority?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ExpenseNotificationsResponse> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.page) params.append('page', options.page.toString());
    if (options?.type) params.append('type', options.type);
    if (options?.status) params.append('status', options.status);
    if (options?.priority) params.append('priority', options.priority);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    
    const queryString = params.toString();
    const url = queryString ? `/expenses/notifications?${queryString}` : '/expenses/notifications';
    
    const response = await apiClient.get<ExpenseNotificationsResponse>(url);
    return response.data;
  }

  // Mark expense notification as read
  static async markExpenseNotificationAsRead(notificationId: string): Promise<void> {
    await apiClient.put(`/expenses/notifications/${notificationId}/read`);
  }

  // Dismiss expense notification
  static async dismissExpenseNotification(notificationId: string): Promise<void> {
    await apiClient.put(`/expenses/notifications/${notificationId}/dismiss`);
  }

  // Snooze expense notification
  static async snoozeExpenseNotification(notificationId: string, snoozeHours: number): Promise<void> {
    await apiClient.put(`/expenses/notifications/${notificationId}/snooze`, { snoozeHours });
  }

  // Get expense notification preferences
  static async getExpenseNotificationPreferences(): Promise<ExpenseNotificationPreferences> {
    const response = await apiClient.get<ExpenseNotificationPreferences>('/expenses/notifications/preferences');
    return response.data;
  }

  // Update expense notification preferences
  static async updateExpenseNotificationPreferences(preferences: Partial<ExpenseNotificationPreferences>): Promise<ExpenseNotificationPreferences> {
    const response = await apiClient.put<ExpenseNotificationPreferences>('/expenses/notifications/preferences', preferences);
    return response.data;
  }

  // Get overdue expense count for badge display
  static async getOverdueExpenseCount(): Promise<{ count: number; overdueReminders: ExpenseReminderNotification[] }> {
    const response = await apiClient.get<{ count: number; overdueReminders: ExpenseReminderNotification[] }>('/expenses/notifications/overdue-count');
    return response.data;
  }

  // ===== RECEIPT MANAGEMENT =====

  // Upload receipt for an expense
  static async uploadReceipt(expenseId: string, file: File): Promise<string> {
    // Get presigned URL for upload
    const response = await apiClient.post<{ uploadUrl: string; receiptUrl: string }>(`/expenses/${expenseId}/receipt`, {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size
    });

    const { uploadUrl, receiptUrl } = response.data;

    // Upload file to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload receipt');
    }

    return receiptUrl;
  }

  // Delete receipt from an expense
  static async deleteReceipt(expenseId: string): Promise<void> {
    await apiClient.delete(`/expenses/${expenseId}/receipt`);
  }

  // ===== CACHE MANAGEMENT =====

  // Clear all cached responses
  static clearCache(): void {
    requestCache.clear();
  }

  // Clear specific cache entries
  static clearCacheForPattern(pattern: string): void {
    for (const key of requestCache.keys()) {
      if (key.includes(pattern)) {
        requestCache.delete(key);
      }
    }
  }

  // Get cache statistics
  static getCacheStats(): { size: number; keys: string[] } {
    return {
      size: requestCache.size,
      keys: Array.from(requestCache.keys()),
    };
  }

  // ===== VALIDATION AND UTILITIES =====

  // Validate expense data before submission
  static validateExpense(expenseData: Partial<CreateExpenseRequest>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check required fields
    if (!expenseData.categoryId?.trim()) {
      errors.push('Category is required');
    }

    if (!expenseData.amount || expenseData.amount <= 0) {
      errors.push('Amount must be greater than 0');
    }

    if (!expenseData.currency?.trim()) {
      errors.push('Currency is required');
    }

    if (!expenseData.date?.trim()) {
      errors.push('Date is required');
    } else {
      // Validate date format
      const date = new Date(expenseData.date);
      if (isNaN(date.getTime())) {
        errors.push('Invalid date format');
      }
    }

    if (!expenseData.recurrenceType) {
      errors.push('Recurrence type is required');
    }

    // Validate recurrence config if provided
    if (expenseData.recurrenceConfig) {
      const config = expenseData.recurrenceConfig;
      
      if (!config.frequency) {
        errors.push('Recurrence frequency is required');
      }

      if (!config.startDate?.trim()) {
        errors.push('Start date is required for recurring expenses');
      }

      if (expenseData.recurrenceType === 'fixed_recurring' && (!config.amount || config.amount <= 0)) {
        errors.push('Amount is required for fixed recurring expenses');
      }

      if (expenseData.recurrenceType === 'variable_recurring' && !config.estimatedAmountRange) {
        errors.push('Estimated amount range is required for variable recurring expenses');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Validate recurring expense data
  static validateRecurringExpense(recurringExpenseData: Partial<CreateRecurringExpenseRequest>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!recurringExpenseData.categoryId?.trim()) {
      errors.push('Category is required');
    }

    if (!recurringExpenseData.recurrenceType) {
      errors.push('Recurrence type is required');
    }

    if (!recurringExpenseData.frequency) {
      errors.push('Frequency is required');
    }

    if (!recurringExpenseData.startDate?.trim()) {
      errors.push('Start date is required');
    }

    if (recurringExpenseData.recurrenceType === 'fixed_recurring' && (!recurringExpenseData.amount || recurringExpenseData.amount <= 0)) {
      errors.push('Amount is required for fixed recurring expenses');
    }

    if (recurringExpenseData.recurrenceType === 'variable_recurring' && !recurringExpenseData.estimatedAmountRange) {
      errors.push('Estimated amount range is required for variable recurring expenses');
    }

    if (recurringExpenseData.endDate && recurringExpenseData.startDate) {
      const startDate = new Date(recurringExpenseData.startDate);
      const endDate = new Date(recurringExpenseData.endDate);
      if (endDate <= startDate) {
        errors.push('End date must be after start date');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Validate expense category data
  static validateExpenseCategory(categoryData: Partial<CreateExpenseCategoryRequest>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!categoryData.name?.trim()) {
      errors.push('Category name is required');
    } else if (categoryData.name.length > 50) {
      errors.push('Category name must be 50 characters or less');
    }

    if (categoryData.description && categoryData.description.length > 200) {
      errors.push('Description must be 200 characters or less');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Format currency amount for display
  static formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  // Calculate next due date for recurring expenses
  static calculateNextDueDate(frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly', currentDate: Date): Date {
    const nextDate = new Date(currentDate);
    
    switch (frequency) {
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
    }
    
    return nextDate;
  }

  // Get expense statistics for a date range
  static async getExpenseStatistics(startDate: string, endDate: string, listingIds?: string[]): Promise<{
    totalAmount: number;
    expenseCount: number;
    averageExpense: number;
    categoryBreakdown: Array<{ categoryName: string; amount: number; percentage: number }>;
    monthlyTrend: Array<{ month: string; amount: number }>;
  }> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    
    if (listingIds && listingIds.length > 0) {
      listingIds.forEach(id => params.append('listingIds', id));
    }

    const response = await apiClient.get<{
      totalAmount: number;
      expenseCount: number;
      averageExpense: number;
      categoryBreakdown: Array<{ categoryName: string; amount: number; percentage: number }>;
      monthlyTrend: Array<{ month: string; amount: number }>;
    }>(`/expenses/statistics?${params.toString()}`);
    return response.data;
  }
}

export default ExpensesService;